import { IProductsImportImagesAction, IProductsImportImagesData } from 'models/product/productsimportimages';
import { IStore } from 'redux/IStore';
import { IProductsImportImage } from 'models/product/productsimportimage';
import { IImageData, IPage } from 'models/common';
import * as c from 'models/constants';
import * as i18n from 'i18next';

/** Action Types */
export const REQUEST_START: string = 'productsimportimages/REQUEST_START';
export const REQUEST_FAILURE: string = 'productsimportimages/REQUEST_FAILURE';
export const SET_IMAGES: string = 'productsimportimages/SET_IMAGES';
export const SET_SELECTED_IMAGES: string = 'productsimportimages/SET_SELECTED_IMAGES';
export const SET_ITEMS_PAGE: string = 'productsimportimages/SET_ITEMS_PAGE';
export const SET_SEARCH_KEYWORD: string = 'productsimportimages/SET_SEARCH_KEYWORD';

/** Initial State */
const initialState: IProductsImportImagesData = {
	images: [],
	selectedImages: [],
	itemsPage: {
		currentPage: 1,
		totalPages: 0,
		totalItems: 0,
		itemsPerPage: 10,
		items: [],
	},
	searchKeyword: '',
};

/** Reducer */
export function productsImportImagesReducer(state = initialState, action: IProductsImportImagesAction) {
	switch (action.type) {
		case REQUEST_START:
			return Object.assign({}, state, {
				isFetching: true,
				error: false,
			});

		case REQUEST_FAILURE:
			return Object.assign({}, state, {
				isFetching: false,
				message: action.payload.message,
				error: true,
			});

		case SET_IMAGES:
			return Object.assign({}, state, {
				images: action.payload.images,
			});

		case SET_SELECTED_IMAGES:
			return Object.assign({}, state, {
				selectedImages: action.payload.selectedImages,
			});

		case SET_ITEMS_PAGE:
			return Object.assign({}, state, {
				itemsPage: action.payload.itemsPage,
			});

		case SET_SEARCH_KEYWORD:
			return Object.assign({}, state, {
				searchKeyword: action.payload.searchKeyword,
			});

		default:
			return state;
	}
}

/** Action Creator */
export function addSelectedImages(filesToUpload: any, rejectedFiles: any) {
	return (dispatch, getState: () => IStore) => {
		let newImagesArray = [];
		const imagesArray = getState().productsImportImages.images;

		let index: number = 0;

		filesToUpload.forEach((image: any) => {
			newImagesArray.push({
				file: image,
				error: false,
				isFetching: false,
				message: null,
				uploaded: false,
				url: '',
				id: index.toString() + new Date().getTime().toString(),
			});

			index++;
		});

		rejectedFiles.forEach((file: any) => {
			let message = null;
			if (file.size > c.MAX_PRODUCT_IMAGE_SIZE) {
				message = {
					status: 422,
					statusText: i18n.t('FileUpload_Error_FileSizeTooLarge'),
					error: [i18n.t('FileUpload_Error_FileSizeTooLarge')],
				};
			} else if (['image/jpeg', 'image/png', 'image/bmp', 'image/gif'].indexOf(file.type) === -1) {
				message = {
					status: 422,
					statusText: i18n.t('FileUpload_Error_FileTypeNotSupported'),
					error: [i18n.t('FileUpload_Error_FileTypeNotSupported')],
				};
			}

			newImagesArray.push({
				file,
				error: true,
				isFetching: false,
				message,
				uploaded: false,
				url: '',
				id: index.toString() + new Date().getTime().toString(),
			});

			index++;
		});

		newImagesArray = newImagesArray.concat(imagesArray);
		dispatch(setImages(newImagesArray));

		dispatch(
			setItemsPage({
				currentPage: 1,
				totalItems: newImagesArray.length,
				itemsPerPage: 10,
				totalPages: Math.ceil(newImagesArray.length / 10),
				items: newImagesArray,
			}),
		);

		dispatch(setSearchKeyword(''));
		dispatch(setSelectedImages([]));
		dispatch(uploadImages());
	};
}

/** Action Creator */
export function uploadImages() {
	return (dispatch, getState: () => any) => {
		const images = getState().productsImportImages.images;
		const imagesToUpload: IProductsImportImage[] = images.filter(
			(x) => x.isFetching === false && !x.uploaded && !x.error,
		);
		let imageToUpload: IProductsImportImage = null;

		if (imagesToUpload && imagesToUpload.length > 0) {
			imageToUpload = imagesToUpload[0];
		}

		if (imageToUpload) {
			const formData = new FormData();
			formData.append('file', imageToUpload.file);

			dispatch(startUploading(imageToUpload.id));

			return fetch(getState().fetchDefault.baseAPI + 'api/imageupload/', {
				method: 'POST',
				body: formData,
				credentials: 'include',
				headers: {
					Authorization: getState().fetchDefault.requestInit.headers.Authorization,
				},
			})
				.then((res) => {
					if (res.ok) {
						return res.json().then((res: IImageData) => {
							dispatch(finishUploading(imageToUpload.id, res.href));
						});
					} else {
						const status: number = res.status;
						const statusText: string = res.statusText;
						return res
							.json()
							.then((res) => {
								dispatch(uploadFailed(imageToUpload.id, res));
							})
							.catch(() => {
								const message: any = { status, statusText };
								dispatch(uploadFailed(imageToUpload.id, message));
							});
					}
				})
				.catch((err) => {
					dispatch(uploadFailed(imageToUpload.id, err));
				});
		}
	};
}

/** Action Creator */
export function startUploading(imageToUploadId: string) {
	return (dispatch, getState: () => IStore) => {
		const images = getState().productsImportImages.images;
		const imagesIndex = images.findIndex((x) => x.id === imageToUploadId);
		images[imagesIndex].isFetching = true;
		dispatch(setImages(images));

		const itemsPage = getState().productsImportImages.itemsPage;
		const itemsPageIndex = itemsPage.items.findIndex((x) => x.id === imageToUploadId);
		itemsPage.items[itemsPageIndex].isFetching = true;
		dispatch(setItemsPage(itemsPage));
	};
}

/** Action Creator */
export function finishUploading(imageToUploadId: string, url: string) {
	return (dispatch, getState: () => IStore) => {
		const images = getState().productsImportImages.images;
		const imagesIndex = images.findIndex((x) => x.id === imageToUploadId);
		images[imagesIndex].isFetching = false;
		images[imagesIndex].uploaded = true;
		images[imagesIndex].url = url;
		dispatch(setImages(images));

		const itemsPage = getState().productsImportImages.itemsPage;
		const itemsPageIndex = itemsPage.items.findIndex((x) => x.id === imageToUploadId);
		if (itemsPageIndex > -1) {
			itemsPage.items[itemsPageIndex] = images[imagesIndex];
			dispatch(setItemsPage(itemsPage));
		}

		dispatch(setItemsPage(itemsPage));
		dispatch(uploadImages());
	};
}

/** Action Creator */
export function uploadFailed(imageToUploadId: string, message: any) {
	return (dispatch, getState: () => IStore) => {
		const images = getState().productsImportImages.images;
		const imagesIndex = images.findIndex((x) => x.id === imageToUploadId);
		images[imagesIndex].isFetching = false;
		images[imagesIndex].error = true;
		images[imagesIndex].message = message;
		dispatch(setImages(images));

		const itemsPage = getState().productsImportImages.itemsPage;
		const itemsPageIndex = itemsPage.items.findIndex((x) => x.id === imageToUploadId);
		if (itemsPageIndex > -1) {
			itemsPage.items[itemsPageIndex] = images[imagesIndex];
			dispatch(setItemsPage(itemsPage));
		}

		dispatch(uploadImages());
	};
}

/** Action Creator */
export function deleteImage(id: string) {
	return (dispatch, getState: () => IStore) => {
		const images = getState().productsImportImages.images;
		const imagesIndex = images.findIndex((x) => x.id === id);
		images.splice(imagesIndex, 1);
		dispatch(setImages(images));

		const itemsPage = getState().productsImportImages.itemsPage;
		const itemsPageIndex = itemsPage.items.findIndex((x) => x.id === id);
		itemsPage.items.splice(itemsPageIndex, 1);
		itemsPage.currentPage = 1;
		itemsPage.totalItems = itemsPage.items.length;
		itemsPage.totalPages = Math.ceil(itemsPage.items.length / 10);
		dispatch(setItemsPage(itemsPage));

		const selectedImages = getState().productsImportImages.selectedImages;
		const selectedImagesIndex = selectedImages.findIndex((x) => x === id);
		selectedImages.splice(selectedImagesIndex, 1);
		dispatch(setSelectedImages(selectedImages));
	};
}

/** Action Creator */
export function deleteSelectedImages() {
	return (dispatch, getState: () => IStore) => {
		const images = getState().productsImportImages.images;
		const selectedImages = getState().productsImportImages.selectedImages;
		const newImagesArray = images.filter((image) => {
			const value = selectedImages.find((y) => image.id === y);
			if (value !== undefined) {
				return false;
			} else {
				return true;
			}
		});
		dispatch(setImages(newImagesArray));

		const itemsPage = getState().productsImportImages.itemsPage;
		const newItemsPageImagesArray = itemsPage.items.filter((image) => {
			const value = selectedImages.find((y) => image.id === y);
			if (value !== undefined) {
				return false;
			} else {
				return true;
			}
		});
		itemsPage.items = newItemsPageImagesArray;
		itemsPage.currentPage = 1;
		itemsPage.totalItems = itemsPage.items.length;
		itemsPage.totalPages = Math.ceil(itemsPage.items.length / 10);
		dispatch(setItemsPage(itemsPage));

		dispatch(setSelectedImages([]));
	};
}

/** Action Creator */
export function selectImage(id: string, select: boolean) {
	return (dispatch, getState: () => IStore) => {
		const selectedImages: string[] = getState().productsImportImages.selectedImages;
		if (select) {
			if (selectedImages.filter((x) => x === id).length === 0) {
				selectedImages.push(id);
			}
		} else {
			if (selectedImages.filter((x) => x === id).length > 0) {
				selectedImages.splice(selectedImages.indexOf(id), 1);
			}
		}

		dispatch(setSelectedImages(selectedImages));
	};
}

/** Action Creator */
export function selectAllImages(select: boolean) {
	return (dispatch, getState: () => IStore) => {
		let selectedImages: string[] = [];

		if (select) {
			selectedImages = getState().productsImportImages.itemsPage.items.map((image) => {
				return image.id;
			});
		}

		dispatch(setSelectedImages(selectedImages));
	};
}

/** Action Creator */
export function loadMore() {
	return (dispatch, getState: () => IStore) => {
		const itemsPage: IPage = getState().productsImportImages.itemsPage;
		itemsPage.currentPage = itemsPage.currentPage + 1;
		dispatch(setItemsPage(itemsPage));
	};
}

/** Action Creator */
export function searchImages() {
	return (dispatch, getState: () => IStore) => {
		const searchKeyword = getState().productsImportImages.searchKeyword;
		const images = getState().productsImportImages.images;
		let searchedImages = [];
		if (searchKeyword.length > 0) {
			images.forEach((image: IProductsImportImage) => {
				if (image.file.name.toLowerCase().indexOf(searchKeyword.toLowerCase()) > -1) {
					searchedImages.push(image);
				}
			});
		} else {
			searchedImages = images;
		}

		const itemsPage: IPage = getState().productsImportImages.itemsPage;
		itemsPage.items = searchedImages;
		itemsPage.currentPage = 1;
		itemsPage.totalItems = itemsPage.items.length;
		itemsPage.totalPages = Math.ceil(itemsPage.items.length / 10);
		dispatch(setItemsPage(itemsPage));

		dispatch(setSelectedImages([]));
	};
}

export function downloadCSVSample() {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		return fetch(
			getState().fetchDefault.baseAPI + 'api/product/CsvSample',
			Object.assign({}, getState().fetchDefault.requestInit),
		)
			.then((res) => {
				if (res.ok) {
					return res.blob().then((blob: any) => {
						const objectUrl = window.URL.createObjectURL(blob);
						const anchor = document.createElement('a');
						document.body.appendChild(anchor);
						anchor.href = objectUrl;
						anchor.download = 'ProductsCsvSample.csv';
						anchor.click();

						window.URL.revokeObjectURL(objectUrl);
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator */
export function requestStart(): IProductsImportImagesAction {
	return {
		type: REQUEST_START,
	};
}

/** Action Creator */
export function requestFailure(message: any): IProductsImportImagesAction {
	return {
		type: REQUEST_FAILURE,
		payload: {
			message,
		},
	};
}

/** Action Creator */
export function setImages(images: IProductsImportImage[]): IProductsImportImagesAction {
	return {
		type: SET_IMAGES,
		payload: {
			images,
		},
	};
}

export function setSelectedImages(selectedImages: string[]): IProductsImportImagesAction {
	return {
		type: SET_SELECTED_IMAGES,
		payload: {
			selectedImages,
		},
	};
}

export function setItemsPage(itemsPage: IPage): IProductsImportImagesAction {
	return {
		type: SET_ITEMS_PAGE,
		payload: {
			itemsPage,
		},
	};
}

export function setSearchKeyword(searchKeyword: string): IProductsImportImagesAction {
	return {
		type: SET_SEARCH_KEYWORD,
		payload: {
			searchKeyword,
		},
	};
}
